import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from './payment';

/**
 * Receipt Settings — the issuer block (name / address / email / phone / GSTIN / logo) printed on
 * every payment receipt. Two kinds of rows:
 *   - Main / Partner  — clientId == null; used by the main API and as the default for everything.
 *   - Client-specific — clientId == <a client>; used by the Client microservice for that client's members.
 *
 * Resolution at print time: client-specific active row (if any) → Main/Partner active row → appsettings
 * Receipt:* defaults. When more than one active row exists for the same scope, the highest Id wins — so
 * keep one active row per scope.
 *
 * Reads are normalized defensively (PascalCase → camelCase) since the backend may serialize Dapper
 * dynamic rows (PascalCase) or a typed DTO (camelCase). Writes POST a camelCase DTO (bound
 * case-insensitively). Insert/Update are POST with the DTO; Enable/Disable/Delete are GET with ?id=.
 */

// ===================== models =====================
export interface ReceiptSettings {
  id: number;
  clientId?: number | null; // null => Main / Partner
  clientName?: string | null; // joined from Client_Master (null for the main row)
  issuerName: string;
  issuerAddress?: string | null;
  issuerEmail?: string | null;
  issuerPhone?: string | null;
  issuerGstin?: string | null;
  logoUrl?: string | null;
  isActive: boolean;
  createdDate: string;
  createdBy?: number | null;
  modifiedDate?: string | null;
  modifiedBy?: number | null;
}
export interface ReceiptSettingsDto {
  id: number; // 0 => insert, >0 => update
  clientId?: number | null; // null/0 => Main / Partner; a client id => that client (Client microservice)
  issuerName: string; // required
  issuerAddress?: string | null;
  issuerEmail?: string | null;
  issuerPhone?: string | null;
  issuerGstin?: string | null;
  logoUrl?: string | null;
  isActive: boolean; // default true
}

// ===================== PascalCase → camelCase normalization =====================
function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asNum(v: any, dflt = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}
function asNumOrNull(v: any): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function asBool(v: any, dflt = false): boolean {
  if (v == null) return dflt;
  return v === true || v === 'true' || v === 1 || v === '1';
}
function asStr(v: any): string | null {
  return v == null ? null : String(v);
}

function mapRow(x: any): ReceiptSettings {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    clientId: asNumOrNull(pick(x, 'ClientId', 'clientId')),
    clientName: asStr(pick(x, 'ClientName', 'clientName')),
    issuerName: pick(x, 'IssuerName', 'issuerName') ?? '',
    issuerAddress: asStr(pick(x, 'IssuerAddress', 'issuerAddress')),
    issuerEmail: asStr(pick(x, 'IssuerEmail', 'issuerEmail')),
    issuerPhone: asStr(pick(x, 'IssuerPhone', 'issuerPhone')),
    issuerGstin: asStr(pick(x, 'IssuerGstin', 'issuerGstin', 'IssuerGSTIN', 'issuerGSTIN')),
    logoUrl: asStr(pick(x, 'LogoUrl', 'logoUrl')),
    isActive: asBool(pick(x, 'IsActive', 'isActive'), true),
    createdDate: pick(x, 'CreatedDate', 'createdDate') ?? '',
    createdBy: asNumOrNull(pick(x, 'CreatedBy', 'createdBy')),
    modifiedDate: asStr(pick(x, 'ModifiedDate', 'modifiedDate')),
    modifiedBy: asNumOrNull(pick(x, 'ModifiedBy', 'modifiedBy')),
  };
}

/** Apply a row-mapper to an envelope whose `data` is an array. */
function mapList(res: any): ApiEnvelope<ReceiptSettings[]> {
  return res && Array.isArray(res.data) ? { ...res, data: res.data.map(mapRow) } : { ...res, data: [] };
}
/** Apply a row-mapper to an envelope whose `data` is a single object. */
function mapObj(res: any): ApiEnvelope<ReceiptSettings> {
  return res?.data && typeof res.data === 'object' ? { ...res, data: mapRow(res.data) } : res;
}

@Injectable({ providedIn: 'root' })
export class ReceiptSettingsService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl; // ends in /api

  all() {
    return this.http
      .get<ApiEnvelope<any[]>>(`${this.api}/ReceiptSettings/GetAll`)
      .pipe(map(mapList));
  }
  get(id: number) {
    return this.http
      .get<ApiEnvelope<any>>(`${this.api}/ReceiptSettings/GetById`, { params: { id } })
      .pipe(map(mapObj));
  }
  /** A client's own row(s) PLUS the Main / Partner default — "what applies to this client". */
  byClient(clientId: number) {
    return this.http
      .get<ApiEnvelope<any[]>>(`${this.api}/ReceiptSettings/GetByClient`, { params: { clientId } })
      .pipe(map(mapList));
  }
  insert(m: ReceiptSettingsDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/ReceiptSettings/Insert`, m);
  }
  update(m: ReceiptSettingsDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/ReceiptSettings/Update`, m);
  }
  enable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/ReceiptSettings/Enable`, { params: { id } });
  }
  disable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/ReceiptSettings/Disable`, { params: { id } });
  }
  delete(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/ReceiptSettings/Delete`, { params: { id } });
  }
}
